import { useState, useRef, useEffect, useCallback } from 'react'
import ReactMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import { getSupabase, getActiveSession, checkSupabaseConnection, getUserSubscription as getSubscription } from '../utils/supabaseClient'
import Link from 'next/link'
import { useRouter } from 'next/router'
import ProfileButton from '../components/ProfileButton'
import CreditWarningBanner from '../components/CreditWarningBanner'

// Event untuk modal upgrade dan memberitahu perubahan kredit
// Inisialisasi event secara lazy di dalam useEffect untuk memastikan hanya dijalankan di browser
let showUpgradeModalEvent;
let creditUpdatedEvent;

export default function Humanize() {
  const [inputText, setInputText] = useState('')
  const [result, setResult] = useState('')
  const [loading, setLoading] = useState(false)
  const [copySuccess, setCopySuccess] = useState(false)
  const [saveStatus, setSaveStatus] = useState('')
  const [title, setTitle] = useState('')
  const [charCount, setCharCount] = useState(0)
  const [isOverLimit, setIsOverLimit] = useState(false)
  const [userSubscription, setUserSubscription] = useState(null)
  const [loadingSubscription, setLoadingSubscription] = useState(true)
  const [showCreditWarning, setShowCreditWarning] = useState(false)
  const [creditWarningMessage, setCreditWarningMessage] = useState('')
  const [showLogin, setShowLogin] = useState(false)
  const [userData, setUserData] = useState(null)
  
  // Menyembunyikan loading overlay saat halaman dimuat
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const loadingOverlay = document.querySelector('.article-loading-overlay');
      if (loadingOverlay) {
        loadingOverlay.classList.remove('active');
        loadingOverlay.classList.add('article-loading-exit');
        setTimeout(() => {
          loadingOverlay.classList.remove('article-loading-exit');
        }, 800);
      }
    }
  }, []);
  
  // Inisialisasi CustomEvent di browser saja
  useEffect(() => {
    if (typeof window !== 'undefined') {
      showUpgradeModalEvent = new CustomEvent('showUpgradeModal');
      creditUpdatedEvent = new CustomEvent('creditUpdated');
    }
  }, []);
  
  const MAX_CHARS = 50000
  const router = useRouter()
  
  const resultRef = useRef(null)

  // Menghitung jumlah karakter saat input berubah
  useEffect(() => {
    const chars = inputText.length
    setCharCount(chars)
    setIsOverLimit(chars > MAX_CHARS)
  }, [inputText])

  // Event listener untuk auto-refresh halaman saat pengguna kembali ke tab
  // useEffect event listener visibilitychange DINONAKTIFKAN untuk diagnosis loop
  /*
  useEffect(() => {
    // ... seluruh isi event listener visibilitychange ...
  }, []);
  */

  // Memuat data langganan pengguna saat pertama kali komponen dimuat
  useEffect(() => {
    fetchUserSubscription();
  }, []);

  // Fungsi untuk mendapatkan info langganan pengguna, dibungkus useCallback agar referensi stabil
  const fetchUserSubscription = useCallback(async () => {
    setLoadingSubscription(true);
    try {
      // Verifikasi sesi dengan fungsi yang lebih robust
      const session = await getActiveSession();
      if (!session?.user?.id) {
        console.log('Tidak ada user ID valid di humanize.js');
        setUserSubscription(null);
        setLoadingSubscription(false);
        return;
      }
      
      // Simpan data user ke state
      setUserData(session.user);
      
      // Gunakan fungsi getUserSubscription yang disediakan oleh supabaseClient
      const { data, error } = await getSubscription(session.user.id);
      
      if (error) {
        console.error('Error getting user subscription:', error);
        setUserSubscription(null);
      } else if (data) {
        console.log('Data langganan ditemukan:', data.plan_name, 'dengan', data.credits_remaining, 'kredit tersisa');
        setUserSubscription(data);
      } else {
        console.log('Tidak ada data langganan yang aktif');
        setUserSubscription(null);
      }
    } catch (error) {
      console.error('Error retrieving subscription:', error);
      setUserSubscription(null);
    } finally {
      setLoadingSubscription(false);
    }
  }, []);

  // Handler untuk input text
  const handleInputChange = (e) => {
    const newText = e.target.value
    const chars = newText.length
    
    if (chars <= MAX_CHARS) {
      setInputText(newText)
      setIsOverLimit(false)
    } else {
      // Tetap update teks tapi tandai sebagai over limit
      setInputText(newText)
      setIsOverLimit(true)
    }
  }

  // Fungsi untuk memeriksa kredit pengguna
  const checkUserCredit = async (creditNeeded = 1) => {
    console.log('checkUserCredit called');
    try {
      // Ambil data sesi pengguna
      const { data: { session } } = await getSupabase().auth.getSession();
      if (!session) {
        return { success: false, error: 'Anda harus login untuk menggunakan fitur ini' };
      }
      
      // Ambil data langganan pengguna
      const { data, error } = await getSupabase()
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', session.user.id)
        .single();
      
      if (error) {
        console.error('Error checking subscription:', error);
        
        // Jika tidak dapat terhubung ke server, kembalikan error koneksi khusus
        if (error.message.includes('network') || error.message.includes('Failed to fetch')) {
          return { 
            success: false, 
            error: 'Tidak dapat terhubung ke server. Coba periksa koneksi internet Anda.', 
            isConnectionError: true 
          };
        }
        
        return { success: false, error: 'Tidak ada langganan aktif. Silakan beli paket terlebih dahulu.' };
      }
      
      if (!data) {
        return { success: false, error: 'Anda belum memiliki paket. Silakan beli paket terlebih dahulu.' };
      }
      
      // Periksa jumlah kredit yang tersisa
      if (data.credits_remaining < creditNeeded) {
        // Jika kredit benar-benar habis (0 atau kurang), tampilkan banner
        if (data.credits_remaining <= 0) {
          // Pesan spesifik untuk paket Free
          if (data.plan_name === 'FREE') {
            return { 
              success: false, 
              error: 'Kredit gratis Anda telah habis. Silakan upgrade ke paket berbayar untuk mendapatkan lebih banyak kredit.', 
              isFreePackage: true 
            };
          }
          return { success: false, error: 'Kredit Anda telah habis. Silakan isi ulang kredit Anda.' };
        } 
        // Jika kredit ada tapi tidak cukup, berikan pesan tanpa menampilkan banner
        else {
          return { 
            success: false, 
            error: `Kredit tidak cukup. Anda membutuhkan ${creditNeeded} kredit, tapi hanya memiliki ${data.credits_remaining} kredit.`,
            showBanner: false
          };
        }
      }
      
      // Pengguna memiliki kredit yang cukup
      console.log(`Pengguna memiliki kredit yang cukup: ${data.credits_remaining} kredit`);
      return { success: true, subscription: data };
    } catch (error) {
      console.error('Error checking user credit:', error);
      return { success: false, error: 'Terjadi kesalahan saat memeriksa kredit.' };
    }
  };

  // Fungsi untuk memperbarui (mengurangi) kredit pengguna
  const decreaseUserCredit = async (subscriptionData) => {
    try {
      if (!subscriptionData || !subscriptionData.id) {
        console.error('Data langganan tidak valid untuk memperbarui kredit');
        return false;
      }
      
      // Kurangi kredit yang tersisa
      const newCreditsRemaining = subscriptionData.credits_remaining - 1;
      
      // Update data langganan di Supabase
      const { error } = await getSupabase()
        .from('user_subscriptions')
        .update({
          credits_remaining: newCreditsRemaining
        })
        .eq('id', subscriptionData.id);
      
      if (error) {
        console.error('Error mengurangi kredit pengguna:', error);
        return false;
      }
      
      console.log('Berhasil mengurangi kredit. Sisa kredit:', newCreditsRemaining);
      
      // Refresh data langganan di state lokal dengan membuat objek baru
      // JANGAN memanggil getUserSubscription() karena akan menyebabkan loop fetching
      setUserSubscription(prev => {
        if (!prev) return null;
        return {...prev, credits_remaining: newCreditsRemaining};
      });
      
      // Pancarkan event bahwa kredit telah diperbarui untuk memperbarui tampilan di ProfileButton
      // Tambahkan throttling untuk mencegah terlalu banyak event dalam waktu singkat
      if (typeof window !== 'undefined') {
        window.lastCreditEventTime = window.lastCreditEventTime || 0;
        const now = Date.now();
        
        // Jika sudah memancarkan event dalam 3 detik terakhir, skip
        if (now - window.lastCreditEventTime < 3000) {
          console.log('Batasi event kredit, terlalu sering dalam waktu singkat');
        } else {
          window.lastCreditEventTime = now;
          if (creditUpdatedEvent) {
            document.dispatchEvent(creditUpdatedEvent);
          }
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error dalam fungsi decreaseUserCredit:', error);
      return false;
    }
  };

  // Fungsi untuk memanusiakan teks
  const humanizeText = async () => {
    console.log('humanizeText called');
    if (!inputText || isOverLimit) return
    
    // Periksa kredit pengguna
    const creditCheck = await checkUserCredit(1);
    console.log('Humanize.js: Hasil creditCheck:', creditCheck);
    
    if (!creditCheck.success) {
      // Jika error koneksi, tampilkan pesan error koneksi
      if (creditCheck.isConnectionError) {
        setCreditWarningMessage(creditCheck.error);
        setShowCreditWarning(true);
        console.log('Banner error koneksi ditampilkan dalam humanizeText:', creditCheck.error);
        return;
      }
      
      if (creditCheck.isFreePackage) {
        // Tampilkan banner kredit habis daripada modal di ProfileButton
        console.log('Humanize.js: Menampilkan banner - Free Package');
        const creditMsg = 'Kredit gratis Anda tidak cukup. Silakan upgrade ke paket berbayar untuk mendapatkan lebih banyak kredit.';
        setCreditWarningMessage(creditMsg);
        setShowCreditWarning(true);
        console.log('Humanize.js: Menampilkan banner kredit habis dengan pesan:', creditMsg);
      } else {
        // Tampilkan banner untuk semua jenis pesan error kredit
        console.log('Humanize.js: Menampilkan banner - Error:', creditCheck.error);
        setCreditWarningMessage(creditCheck.error);
        setShowCreditWarning(true);
      }
      return;
    }
    
    // Simpan data subscription untuk digunakan nanti
    const subscriptionData = creditCheck.subscription;
    
    setLoading(true)
    
    // Tambahkan scroll ke hasil jika sudah ada
    if (resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    
    try {
      // Tampilkan animasi loading
      const loadingOverlay = document.querySelector('.article-loading-overlay');
      if (loadingOverlay) {
        loadingOverlay.classList.add('active');
        
        // Perbarui teks loading
        const loadingTitle = document.querySelector('.article-loading-title');
        if (loadingTitle) loadingTitle.textContent = 'Memanusiakan Teks';
        
        const loadingText = document.querySelector('.article-loading-text');
        if (loadingText) loadingText.textContent = 'Sedang memodifikasi teks agar terlihat alami dan lolos dari deteksi AI. Proses ini membutuhkan waktu beberapa saat...';
        
        const loadingStatus = document.querySelector('.article-loading-status');
        if (loadingStatus) loadingStatus.textContent = 'Membuat variasi kalimat dan gaya penulisan...';
      }
      
      // Ekstrak bagian referensi sebelum mengirim ke API humanisasi
      let textContent = inputText;
      let referenceSection = "";
      
      // Cek jika artikel memiliki bagian referensi
      const referencePatterns = [
        /\n\n(References|REFERENCES|Referensi|REFERENSI|Daftar Pustaka|DAFTAR PUSTAKA)[\s\S]*$/i,
        /\n\n(Bibliography|BIBLIOGRAPHY|Bibliografi|BIBLIOGRAFI)[\s\S]*$/i,
        /\n\n(Citations|CITATIONS|Sitasi|SITASI)[\s\S]*$/i,
        /\n\n(## References|## REFERENCES|## Referensi|## REFERENSI|## Daftar Pustaka|## DAFTAR PUSTAKA)[\s\S]*$/i,
        /\n\n(## Bibliography|## BIBLIOGRAPHY|## Bibliografi|## BIBLIOGRAFI)[\s\S]*$/i,
        /\n\n(## Citations|## CITATIONS|## Sitasi|## SITASI)[\s\S]*$/i,
        /\n\n(## Pustaka|## PUSTAKA|## Sumber|## SUMBER)[\s\S]*$/i,
        /\n\n(# References|# REFERENCES|# Referensi|# REFERENSI|# Daftar Pustaka|# DAFTAR PUSTAKA)[\s\S]*$/i,
        /\n(<div class=["']reference-list["'][\s\S]*$)/i,
        /\n(<div class=['"]reference-list['"][\s\S]*$)/i,
        /\n\n(Sumber Referensi|SUMBER REFERENSI)[\s\S]*$/i,
        /\n\n(Sumber|SUMBER)[\s\S]*$/i,
        /\n\n([0-9]+\.\s+https?:\/\/)[\s\S]*$/i, // Deteksi daftar nomor dengan URL
        /\n\n(\[\d+\]\s+)[\s\S]*$/i // Deteksi referensi format [1] Judul
      ];
      
      // Cari pola referensi
      for (const pattern of referencePatterns) {
        const match = inputText.match(pattern);
        if (match) {
          referenceSection = match[0];
          textContent = inputText.replace(referenceSection, ''); // Hapus bagian referensi dari konten
          console.log('Bagian referensi ditemukan dan dipisahkan untuk dipertahankan');
          break;
        }
      }
      
      // Kirim hanya konten artikel ke API humanisasi
      const response = await fetch('/api/humanize-text', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text: textContent })
      })
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || `Error: ${response.status} ${response.statusText}`;
        throw new Error(errorMessage);
      }
      
      console.log('Sebelum response.json()');
      const data = await response.json()
      console.log('Setelah response.json()');
      console.log('API response:', data);
      
      // Sembunyikan animasi loading
      if (loadingOverlay) {
        loadingOverlay.classList.add('article-loading-exit');
        setTimeout(() => {
          loadingOverlay.classList.remove('active', 'article-loading-exit');
        }, 800);
      }
      
      // Gabungkan kembali konten yang dihumanisasi dengan bagian referensi
      const finalHumanizedText = referenceSection ? data.humanizedText + referenceSection : data.humanizedText;
      
      setResult(finalHumanizedText)
      
      // Kurangi kredit pengguna setelah berhasil menghumanisasi teks
      decreaseUserCredit(subscriptionData);
      
      // Tampilkan notifikasi sukses
      setTimeout(() => {
        if (resultRef.current) {
          const successNotif = document.createElement('div');
          successNotif.className = 'humanize-success-notification';
          successNotif.innerHTML = '✓ Teks berhasil dihumanisasi!';
          resultRef.current.prepend(successNotif);
          
          // Hapus notifikasi setelah beberapa detik
          setTimeout(() => {
            successNotif.classList.add('fade-out');
            setTimeout(() => successNotif.remove(), 500);
          }, 3000);
        }
      }, 500);
      
    } catch (error) {
      console.error('Error humanizing text:', error)
      console.log('Error in humanizeText:', error)
      
      // Sembunyikan animasi loading jika terjadi error
      const loadingOverlay = document.querySelector('.article-loading-overlay');
      if (loadingOverlay) {
        loadingOverlay.classList.add('article-loading-exit');
        setTimeout(() => {
          loadingOverlay.classList.remove('active', 'article-loading-exit');
        }, 800);
      }
      
      // Tampilkan pesan error yang bermakna
      const errorMessage = error.message || 'Terjadi kesalahan saat memanusiakan teks';
      alert(errorMessage);
    } finally {
      setLoading(false)
    }
  }

  const handleCopy = () => {
    if (resultRef.current) {
      // Mengambil HTML dari hasil render markdown
      const contentHTML = resultRef.current.innerHTML;
      
      // Membuat elemen temporer untuk menyimpan HTML
      const tempElement = document.createElement('div');
      tempElement.innerHTML = contentHTML;
      
      // Membuat objek blob dari HTML
      const blob = new Blob([
        '<html><head><meta charset="utf-8"><title>Artikel</title></head><body>',
        tempElement.innerHTML,
        '</body></html>'
      ], { type: 'text/html' });
      
      try {
        // Menggunakan Clipboard API modern dengan format HTML
        const clipboardItem = new ClipboardItem({
          'text/html': blob,
          'text/plain': new Blob([tempElement.innerText], { type: 'text/plain' })
        });
        
        navigator.clipboard.write([clipboardItem])
          .then(() => {
            setCopySuccess(true)
            setTimeout(() => setCopySuccess(false), 2000)
          })
          .catch(err => {
            console.error('Gagal menyalin dengan format rich text:', err);
            // Fallback ke metode sederhana jika gagal
            navigator.clipboard.writeText(tempElement.innerText)
              .then(() => {
                setCopySuccess(true)
                setTimeout(() => setCopySuccess(false), 2000)
              })
              .catch(() => alert('Gagal menyalin teks!'))
          });
      } catch (e) {
        // Fallback untuk browser yang tidak mendukung ClipboardItem
        navigator.clipboard.writeText(tempElement.innerText)
          .then(() => {
            setCopySuccess(true)
            setTimeout(() => setCopySuccess(false), 2000)
          })
          .catch(() => alert('Gagal menyalin teks!'))
      }
    }
  }

  // Fungsi untuk menyimpan artikel ke Supabase
  const saveArticleToSupabase = async () => {
    if (!result || !title) {
      alert('Masukkan judul terlebih dahulu untuk menyimpan hasil humanisasi');
      return;
    }

    // Periksa kredit pengguna
    const creditCheck = await checkUserCredit(0);
    if (!creditCheck.success) {
      // Jika error koneksi, tampilkan pesan error koneksi
      if (creditCheck.isConnectionError) {
        setCreditWarningMessage(creditCheck.error);
        setShowCreditWarning(true);
        console.log('Banner error koneksi ditampilkan dalam saveArticleToSupabase:', creditCheck.error);
        return;
      }
      
      if (creditCheck.isFreePackage) {
        // Tampilkan banner kredit habis daripada modal di ProfileButton
        setCreditWarningMessage('Kredit gratis Anda telah habis. Silakan upgrade ke paket berbayar untuk mendapatkan lebih banyak kredit.');
        setShowCreditWarning(true);
      } else {
        // Tampilkan banner untuk semua jenis pesan error kredit
        setCreditWarningMessage(creditCheck.error);
        setShowCreditWarning(true);
      }
      return;
    }

    // Simpan data subscription untuk digunakan nanti
    const subscriptionData = creditCheck.subscription;

    setSaveStatus('saving')
    
    try {
      // Ambil user ID dari sesi
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Anda harus login untuk menyimpan artikel');
      }
      
      // Simpan artikel dengan flag is_humanized
      const { data, error } = await supabase
        .from('articles')
        .insert([
          { 
            title: title,
            content: result,
            article_type: 'Humanized',
            language: detectLanguage(result),
            writing_style: 'Dihumanisasi',
            created_at: new Date().toISOString(),
            is_humanized: true,
            user_id: session.user.id
          }
        ])
        .select();
      
      if (error) throw error;
      
      // Kurangi kredit pengguna setelah berhasil menyimpan artikel
      // Komenter baris di bawah jika penyimpanan tidak perlu mengurangi kredit, karena kredit
      // sudah berkurang saat proses humanisasi
      // decreaseUserCredit(subscriptionData);
      
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(''), 2000);
    } catch (error) {
      console.error('Error saving article:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(''), 2000);
    }
  }

  // Fungsi sederhana untuk mendeteksi bahasa dari teks
  const detectLanguage = (text) => {
    // Deteksi bahasa sederhana berdasarkan frekuensi kata-kata Indonesia umum
    const indonesianWords = ['yang', 'dan', 'dengan', 'untuk', 'pada', 'dari', 'tidak', 'adalah', 'ini', 'itu', 'dalam', 'akan'];
    
    const lowercaseText = text.toLowerCase();
    let indonesianCount = 0;
    
    indonesianWords.forEach(word => {
      const regex = new RegExp(`\\b${word}\\b`, 'g');
      const matches = lowercaseText.match(regex);
      if (matches) indonesianCount += matches.length;
    });
    
    // Jika terdapat beberapa kata Indonesia umum, asumsikan bahasa Indonesia
    return indonesianCount > 3 ? 'Bahasa Indonesia' : 'English';
  }

  const handleReset = () => {
    setInputText('')
    setResult('')
    setTitle('')
    setSaveStatus('')
  }

  // Tambahkan fungsi handleNavigation untuk hard refresh
  const handleNavigation = (e, path) => {
    e.preventDefault();
    
    // Simpan URL tujuan ke sessionStorage
    sessionStorage.setItem('nextRoute', path);
    
    // Simpan state pengguna jika perlu
    if (userData && userData.id) {
      sessionStorage.setItem('preserveUser', JSON.stringify({
        id: userData.id,
        timestamp: Date.now()
      }));
    }
    
    // Lakukan hard refresh dengan parameter untuk mencegah cache
    window.location.href = path + (path.includes('?') ? '&' : '?') + '_refresh=' + Date.now();
  };

  return (
    <div>
      {/* Header dengan logo */}
      <header className="app-header">
        <div className="header-container">
          <div className="logo-container">
            <Link href="/">
              <img 
                src="https://ratu.ai/wp-content/uploads/2025/04/penulis-pro-ratu-logo.jpg" 
                alt="Penulis Pro Logo" 
                className="app-logo" 
              />
            </Link>
          </div>
          <div className="right-section">
            <div className="nav-links">
              <a 
                href="/" 
                onClick={(e) => handleNavigation(e, '/')}
                className="nav-link"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="nav-icon">
                  <path d="M12 19l7-7 3 3-7 7-3-3z"></path>
                  <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path>
                  <path d="M2 2l7.586 7.586"></path>
                  <circle cx="11" cy="11" r="2"></circle>
                </svg>
                <span>Buat Artikel</span>
              </a>
              <a 
                href="/articles" 
                onClick={(e) => handleNavigation(e, '/articles')}
                className="nav-link"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="nav-icon">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                </svg>
                <span>Artikel Tersimpan</span>
              </a>
              <a 
                href="/humanize" 
                className="nav-link active"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="nav-icon">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="9" y1="8" x2="15" y2="8"></line>
                  <line x1="9" y1="12" x2="15" y2="12"></line>
                  <line x1="9" y1="16" x2="13" y2="16"></line>
                  <line x1="17" y1="17" x2="19" y2="19"></line>
                  <line x1="19" y1="17" x2="17" y2="19"></line>
                </svg>
                <span>Humanize</span>
              </a>
            </div>
            
            <ProfileButton />
          </div>
        </div>
      </header>

      <div className="container">
        <div className="humanize-page">
          <div className="humanize-header">
            <h1>Humanize Teks</h1>
            <p className="humanize-description">
              Alat ini membantu mengubah teks hasil AI agar terlihat lebih manusiawi dan lolos dari berbagai detector AI seperti GPTZero, Winston AI, dan lainnya. Cukup paste teks Anda dan klik tombol "Humanize".
            </p>
          </div>
        
          <div className="humanize-container">
            <div className="humanize-input">
              <div className="humanize-input-header">
                <h2>Teks Input</h2>
                <div className={`char-counter ${charCount > MAX_CHARS * 0.9 && charCount <= MAX_CHARS ? 'warning' : ''} ${isOverLimit ? 'danger' : ''}`}>
                  <span>{charCount}</span>/{MAX_CHARS} karakter
                </div>
              </div>
              <textarea
                value={inputText}
                onChange={handleInputChange}
                placeholder="Paste teks yang ingin dihumanisasi di sini..."
                rows={10}
                className={`humanize-textarea ${isOverLimit ? 'error-border' : ''}`}
              />
              {isOverLimit && (
                <div className="char-limit-error">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <span>Batas maksimal 50.000 karakter. Silakan kurangi teks Anda.</span>
                </div>
              )}
              <div className="humanize-input-actions">
                <button 
                  onClick={humanizeText} 
                  disabled={loading || !inputText || isOverLimit}
                  className={`humanize-button ${loading ? 'loading' : ''} ${isOverLimit ? 'disabled' : ''}`}
                >
                  {loading ? 'Memproses...' : isOverLimit ? 'Melebihi Batas Karakter' : 'Humanize Teks'}
                </button>
              </div>
            </div>
            
            {result && (
              <div className="humanize-result">
                <div className="humanize-result-header">
                  <h2>Hasil Humanisasi</h2>
                  
                  <div className="humanized-badge-container">
                    <div className="humanized-badge">
                      <span className="humanized-icon">✓</span>
                      <span className="humanized-text">AI-Undetectable</span>
                      <div className="humanized-tooltip">Teks ini telah dioptimalkan agar tidak terdeteksi sebagai konten AI oleh detector seperti GPTZero, Winston AI, dan lainnya.</div>
                    </div>
                  </div>
                  
                  {/* Field untuk memasukkan judul (jika ingin menyimpan) */}
                  <div className="title-input-container">
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Masukkan judul untuk menyimpan hasil..."
                      className="title-input"
                    />
                  </div>
                </div>
                
                <div className="humanize-result-content" ref={resultRef}>
                  <ReactMarkdown
                    rehypePlugins={[rehypeRaw]}
                    components={{
                      a: ({node, ...props}) => <a target="_blank" rel="noopener noreferrer" {...props} />
                    }}
                  >
                    {result}
                  </ReactMarkdown>
                </div>
                
                <div className="humanize-result-actions">
                  <button 
                    onClick={handleCopy} 
                    className={`copy-button ${copySuccess ? 'success' : ''}`}
                  >
                    {copySuccess ? '✓ Tersalin!' : 'Salin Hasil'}
                  </button>
                  <button 
                    onClick={saveArticleToSupabase}
                    className={`save-button ${saveStatus ? saveStatus : ''}`}
                    disabled={saveStatus === 'saving' || saveStatus === 'success' || !title}
                  >
                    {saveStatus === 'saving' ? 'Menyimpan...' : 
                     saveStatus === 'success' ? '✓ Tersimpan!' :
                     saveStatus === 'error' ? 'Gagal Menyimpan' :
                     'Simpan Hasil'}
                  </button>
                  <button 
                    onClick={handleReset}
                    className="new-button"
                  >
                    Reset
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Loading Animation Overlay */}
      <div className="article-loading-overlay">
        <div className="article-loading-content">
          <div className="article-loading-icon">
            <div className="article-loading-circle"></div>
            <div className="article-loading-circle"></div>
            <div className="article-loading-circle"></div>
            <div className="article-loading-pulse"></div>
          </div>
          <h3 className="article-loading-title">Humanizing</h3>
          <p className="article-loading-text">Sedang memanusiakan teks Anda untuk menghindari deteksi AI...</p>
          <div className="article-loading-progress">
            <div className="article-loading-progress-bar"></div>
          </div>
          <div className="article-loading-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <div className="article-loading-status">Ini mungkin memerlukan waktu beberapa detik</div>
          <button className="article-loading-exit">Batalkan</button>
        </div>
      </div>

      {/* Banner peringatan kredit */}
      {showCreditWarning && (
        <CreditWarningBanner 
          message={creditWarningMessage}
          isConnectionError={false}
          onClose={() => {
            setShowCreditWarning(false);
          }}
        />
      )}
      
      <style jsx global>{`
        /* Styling untuk character counter dan error message */
        .humanize-input-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
        }
        
        .char-counter {
          font-size: 14px;
          color: #666;
          background: #f5f5f5;
          padding: 4px 10px;
          border-radius: 20px;
          display: inline-flex;
          align-items: center;
          transition: all 0.3s ease;
        }
        
        .char-counter span {
          font-weight: 600;
          margin-right: 2px;
        }
        
        .char-counter.warning {
          background-color: #fff3e0;
          color: #ff9800;
        }
        
        .char-counter.danger {
          background-color: #ffebee;
          color: #f44336;
        }
        
        .humanize-textarea.error-border {
          border-color: #f44336;
          background-color: rgba(244, 67, 54, 0.03);
        }
        
        .char-limit-error {
          display: flex;
          align-items: center;
          margin-top: 8px;
          color: #f44336;
          font-size: 14px;
        }
        
        .char-limit-error svg {
          margin-right: 6px;
          stroke: #f44336;
        }
        
        .humanize-button.disabled {
          background-color: #f5f5f5;
          color: #999;
          cursor: not-allowed;
          border: 1px solid #ddd;
        }
      `}</style>
    </div>
  )
} 
